import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { GraduationCap, Plus, Edit, Trash2, Save, ArrowLeft, Loader2 } from 'lucide-react';
import toast from "react-hot-toast";
import Sidebar from '../../GlobalManagement/Pages/Sidebar';
import SearchBar from '../../GlobalManagement/Pages/SearchBar';
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterAccentButton, 
  masterPrimaryButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell, 
  masterEditButton, 
  masterDeleteButton, 
  masterFormContainer, 
  masterFormSectionHeader, 
  masterFormGrid2, 
  masterFormGridRow, 
  masterInputLabel, 
  masterInput, 
  masterSelect, 
  masterFormActionBlock, 
  masterActiveColor, 
  masterInactiveColor, 
  masterActionButtonsContainer,
  masterReadOnly,
  masterTableDatastatus,
  masterTableCellstatus
} from '../../GlobalManagement/Pages/masterPageStyles';

const QualificationMaster = () => {
  const [qualifications, setQualifications] = useState([]);
  const [showForm, setShowForm] = useState(false); // Toggle between List view and Form view
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18);

  const [formData, setFormData] = useState({
    qualCode: '',
    qualName: '',
    status: 'Active'
  });

  const fetchQualifications = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get('/api/master/qualifications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQualifications(res.data || []);
    } catch (err) {
      toast.error("Failed to load qualifications");
    }
  };

  const fetchNextQualCode = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get('/api/master/qualifications/next-code', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(prev => ({ ...prev, qualCode: res.data.qualCode || 'ED001' }));
    } catch {
      setFormData(prev => ({ ...prev, qualCode: 'ED001' }));
    }
  };

  useEffect(() => {
    fetchQualifications();
  }, []);

  const filteredData = useMemo(() => {
    const sortedList = [...qualifications].sort((a, b) => {
      const idA = String(a.qualCode || a._id || '');
      const idB = String(b.qualCode || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const name = (item.qualName || '').toLowerCase();
      const code = (item.qualCode || '').toLowerCase();
      return name.startsWith(q) || code.startsWith(q);
    });
  }, [qualifications, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const handleAddNew = async () => {
    setEditId(null);
    setFormData({
      qualCode: '',
      qualName: '',
      status: 'Active'
    });
    setShowForm(true);
    await fetchNextQualCode();
  };

  const handleEdit = (qual) => {
    setEditId(qual._id);
    setFormData({
      qualCode: qual.qualCode || '',
      qualName: qual.qualName || '',
      status: qual.status || 'Active'
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this qualification?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/master/qualifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deleted successfully");
      fetchQualifications();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.qualName.trim()) {
      toast.error('Qualification name is required');
      return;
    }

    const duplicate = qualifications.find(
      (q) =>
        q.qualName.toLowerCase().trim() === formData.qualName.toLowerCase().trim() &&
        q._id !== editId
    );
    if (duplicate) {
      toast.error('Qualification already exists!');
      return;
    }

    setIsSaving(true);
    const token = localStorage.getItem("token");
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const payload = {
        qualCode: formData.qualCode,
        qualName: formData.qualName.trim().toUpperCase(),
        status: formData.status
      };

      if (editId) {
        await axios.put(`/api/master/qualifications/${editId}`, payload, config);
        toast.success('Updated successfully');
      } else {
        await axios.post('/api/master/qualifications', payload, config);
        toast.success('Saved successfully');
      }

      fetchQualifications();
      setShowForm(false);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to save/update qualification');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        {/* Header Section */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><GraduationCap size={14} /></div>
              <div><h1 className={masterTitle}>Qualification Master</h1></div>
            </div>
            <button 
              type="button"
              onClick={showForm ? () => setShowForm(false) : handleAddNew} 
              className={`${showForm ? masterPrimaryButton : masterAccentButton} shrink-0 inline-flex items-center gap-1`}
            >
              {showForm ? <><ArrowLeft size={10} /> VIEW LIST</> : <><Plus size={10} /> ADD QUALIFICATION</>}
            </button>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full`}>
          {!showForm ? (
            <div className={`${masterPanel} flex flex-col overflow-hidden w-full`}>
              {/* Search & Pagination Toolbar */}
              <div className="border-b border-slate-200 flex-shrink-0 bg-white p-1 sm:p-2">
                <SearchBar 
                  value={searchTerm} 
                  onChange={(val) => {
                    setSearchTerm(val);
                    setCurrentPage(1);
                  }} 
                  placeholder="Search by qualification name or code..." 
                  totalItems={filteredData.length} 
                  currentPage={currentPage} 
                  pageSize={pageSize} 
                  onPageChange={setCurrentPage} 
                  onPageSizeChange={setPageSize} 
                />
              </div>

              {/* Horizontally Scrollable Table Container */}
              <div className="overflow-x-auto w-full">
                <table className={masterTable}>
                  <thead>
                    <tr className={masterStickyHeadRow}>
                      <th className={masterTableData}>S.No</th>
                      
                      <th className={masterTableData}>Qualification Name</th>
                      <th className={masterTableDatastatus}>Status</th>
                      <th className={masterTableDatastatus}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={masterTbody}>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((qual, index) => (
                        <tr key={qual._id} className={masterTableRow}>
                          <td className={masterTableCell}>
                            {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                          </td>
                         
                          <td className={masterTableCell} style={{ textAlign: 'left', fontWeight: '600' }}>
                            {qual.qualName}
                          </td>
                          <td className={masterTableCellstatus}>
                            <span className={String(qual.status).toLowerCase() === 'active' ? masterActiveColor : masterInactiveColor}>
                              {qual.status || 'Active'}
                            </span>
                          </td>
                          <td className={masterTableCellstatus}>
                            <div className={masterActionButtonsContainer}>
                              <button 
                                type="button" 
                                onClick={() => handleEdit(qual)} 
                                className={masterEditButton}
                                title="Edit"
                              >
                                <Edit size={10} />
                              </button>
                              <button 
                                type="button" 
                                onClick={() => handleDelete(qual._id)} 
                                className={masterDeleteButton}
                                title="Delete"
                              >
                                <Trash2 size={10} /> 
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                          No qualifications found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Add / Edit Form Card */
            <form onSubmit={handleSubmit} className={masterFormContainer}>
              <div className={masterFormSectionHeader}>
                <h2>{editId ? 'Edit Qualification Details' : 'Create New Qualification'}</h2>
              </div>

              <div className={masterFormGrid2}>
               

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Qualification Name <span className="text-rose-500">*</span></label>
                  <input
                    type="text"
                    required
                    autoFocus
                    value={formData.qualName}
                    onChange={(e) => setFormData({...formData, qualName: e.target.value.toUpperCase()})}
                    className={masterInput}
                    placeholder="e.g. BACHELOR OF ARTS"
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className={masterSelect}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className={masterFormActionBlock}>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-3 py-1 rounded border border-slate-300 text-slate-700 font-semibold text-[11px] bg-white hover:bg-slate-50 transition-all uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className={masterAccentButton}
                >
                  {isSaving ? (
                    <Loader2 className="animate-spin" size={10} />
                  ) : (
                    <Save size={10} />
                  )}
                  {editId ? 'UPDATE QUALIFICATION' : 'SAVE QUALIFICATION'}
                </button>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};

export default QualificationMaster;